// @ts-nocheck
import React, { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/layout";
import {
  PageHeader,
  StatusBadge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  showToast,
} from "@/components/ui";
import { ContextBar } from "@/components/catalog";
import { valueMappingData } from "@/data/valueMappingMockData";
import { withAppBase } from "@/lib/appPaths";
import { cn } from "@/lib/utils";
import {
  ArrowRight,
  ArrowLeftRight,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Save,
  X,
  Ban,
  MessageSquare,
  ChevronDown,
  Info,
} from "lucide-react";

const mappingTypeLabels = {
  NUMERIC_TO_ENUM: "Numeric → Enum",
  ENUM_TO_ENUM: "Enum → Enum",
  TEXT_TO_ENUM: "Text → Enum",
};

export default function ValueMapping() {
  const {
    context,
    sourceValues,
    baseValues,
    initialMappings,
    rangeSuggestions,
  } = valueMappingData;

  const [mappings, setMappings] = useState(initialMappings);
  const [unsupportedValues, setUnsupportedValues] = useState(new Set());
  const [notes, setNotes] = useState({});
  const [showNoteFor, setShowNoteFor] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  // Calculate progress
  const totalValues = sourceValues.length;
  const mappedCount = Object.values(mappings).filter((v) => v !== null).length;
  const unsupportedCount = unsupportedValues.size;
  const resolvedCount = mappedCount + unsupportedCount;
  const isComplete = resolvedCount === totalValues;

  // Validation - check for one-to-many (not allowed)
  const mappingCounts = useMemo(() => {
    const counts = {};
    Object.entries(mappings).forEach(([sourceId, baseValue]) => {
      if (baseValue && !unsupportedValues.has(sourceId)) {
        if (!counts[baseValue]) counts[baseValue] = [];
        counts[baseValue].push(sourceId);
      }
    });
    return counts;
  }, [mappings, unsupportedValues]);

  const handleMappingChange = (sourceId, baseValue) => {
    setMappings((prev) => ({ ...prev, [sourceId]: baseValue }));
    // Remove from unsupported if mapping is set
    if (baseValue) {
      setUnsupportedValues((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sourceId);
        return newSet;
      });
    }
  };

  const handleMarkUnsupported = (sourceId) => {
    setUnsupportedValues((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sourceId)) {
        newSet.delete(sourceId);
      } else {
        newSet.add(sourceId);
        // Clear mapping when marking unsupported
        setMappings((p) => ({ ...p, [sourceId]: null }));
      }
      return newSet;
    });
  };

  const handleAddNote = (sourceId, note) => {
    setNotes((prev) => ({ ...prev, [sourceId]: note }));
  };

  const handleSave = () => {
    if (!isComplete) {
      showToast.error(
        "Incomplete Mapping",
        "Please map all values or mark them as unsupported.",
      );
      return;
    }
    setIsSaved(true);
    showToast.success(
      "Value Mapping Saved",
      "All value mappings have been saved successfully.",
    );
  };

  const handleCancel = () => {
    window.location.href = withAppBase("/catalog/mapping");
  };

  // Apply AI suggestion to all unmapped values
  const handleApplyAllSuggestions = () => {
    const newMappings = { ...mappings };
    sourceValues.forEach((sv) => {
      if (!newMappings[sv.id] && !unsupportedValues.has(sv.id) && sv.aiHint) {
        const suggestedValue = sv.aiHint.replace("Suggested: ", "");
        if (baseValues.find((bv) => bv.value === suggestedValue)) {
          newMappings[sv.id] = suggestedValue;
        }
      }
    });
    setMappings(newMappings);
    showToast.success(
      "AI Suggestions Applied",
      "All suggested mappings have been applied.",
    );
  };

  return (
    <DashboardLayout activePath="/catalog/mapping">
      <PageHeader
        title="Attribute Value Mapping"
        subtitle="Map source attribute values to base marketplace values"
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Mapping Review", active: false },
          { label: "Value Mapping", active: true },
        ]}
      />

      <ContextBar />

      {/* Success Banner */}
      {isSaved && (
        <div
          data-testid="success-banner"
          className="mb-6 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          <div className="flex-1">
            <p className="font-medium text-emerald-800 dark:text-emerald-200">
              Value mapping saved successfully
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Status updated to VALUE_MAPPING_COMPLETED
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              (window.location.href = withAppBase("/catalog/mapping"))
            }
            className="text-emerald-700 dark:text-emerald-300"
          >
            Back to Mapping Review
          </Button>
        </div>
      )}

      {/* Context Header Card */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            {/* Attribute Name */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Attribute
              </p>
              <p className="text-lg font-bold font-heading text-slate-900 dark:text-slate-100">
                {context.attributeName}
              </p>
            </div>

            <div className="w-px h-12 bg-slate-200 dark:bg-slate-700" />

            {/* Source Marketplace */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Source
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {context.sourceMarketplace}
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                  {context.sourceAttributeType}
                </span>
              </p>
            </div>

            <ArrowRight className="w-5 h-5 text-slate-400" />

            {/* Base Marketplace */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Base
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {context.baseMarketplace}
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400">
                  {context.baseAttributeType}
                </span>
              </p>
            </div>

            <div className="w-px h-12 bg-slate-200 dark:bg-slate-700" />

            {/* Mapping Type */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Mapping Type
              </p>
              <StatusBadge status="WARNING" showDot={false} className="mt-1">
                <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />
                {mappingTypeLabels[context.mappingType] || context.mappingType}
              </StatusBadge>
            </div>

            <div className="w-px h-12 bg-slate-200 dark:bg-slate-700" />

            {/* Status */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Status
              </p>
              <StatusBadge
                status={isSaved ? "ACTIVE" : "PENDING"}
                showDot
                className="mt-1"
              >
                {isSaved ? "VALUE_MAPPING_COMPLETED" : context.status}
              </StatusBadge>
            </div>

            <div className="flex-1" />

            {/* Progress */}
            <div className="text-right">
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Progress
              </p>
              <p
                className={cn(
                  "text-lg font-bold",
                  isComplete
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-amber-600 dark:text-amber-400",
                )}
              >
                {resolvedCount} / {totalValues}
                <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-1">
                  mapped
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-12 gap-6 mb-24">
        {/* Left Panel - Source Values */}
        <div className="col-span-3">
          <Card className="sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Source Values
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                  ({context.sourceMarketplace})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {sourceValues.map((sv) => (
                  <div
                    key={sv.id}
                    data-testid={`source-value-${sv.id}`}
                    className={cn(
                      "flex items-center justify-between p-2.5 rounded-lg border",
                      unsupportedValues.has(sv.id)
                        ? "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 opacity-50"
                        : mappings[sv.id]
                          ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700",
                    )}
                  >
                    <div>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {sv.value}
                      </span>
                      {sv.unit && (
                        <span className="text-sm text-slate-500 dark:text-slate-400 ml-1">
                          {sv.unit}
                        </span>
                      )}
                    </div>
                    {mappings[sv.id] && !unsupportedValues.has(sv.id) && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    )}
                    {unsupportedValues.has(sv.id) && (
                      <Ban className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center Panel - Mapping Table */}
        <div className="col-span-6">
          {/* AI Suggestions Helper */}
          <Card className="mb-4 bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                  <div>
                    <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                      AI Suggestions Available
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">
                      {rangeSuggestions
                        .map((r) => `${r.range} → ${r.suggested}`)
                        .join(" | ")}
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="apply-suggestions-btn"
                  variant="secondary"
                  size="sm"
                  onClick={handleApplyAllSuggestions}
                  className="bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800"
                >
                  <Sparkles className="w-4 h-4" />
                  Apply All Suggestions
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Mapping Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Value Mappings</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-3 px-3 py-2 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <div className="col-span-3">Source Value</div>
                <div className="col-span-1 text-center"></div>
                <div className="col-span-4">Base Value</div>
                <div className="col-span-2">Hint</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>

              {/* Mapping Rows */}
              <div className="space-y-2">
                {sourceValues.map((sv) => {
                  const isUnsupported = unsupportedValues.has(sv.id);
                  const currentMapping = mappings[sv.id];
                  const hasError = !currentMapping && !isUnsupported;

                  return (
                    <div
                      key={sv.id}
                      data-testid={`mapping-row-${sv.id}`}
                      className={cn(
                        "grid grid-cols-12 gap-3 items-center p-3 rounded-lg border",
                        isUnsupported
                          ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                          : currentMapping
                            ? "bg-white dark:bg-slate-900 border-emerald-200 dark:border-emerald-800"
                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700",
                      )}
                    >
                      {/* Source Value */}
                      <div
                        className={cn(
                          "col-span-3",
                          isUnsupported && "opacity-50",
                        )}
                      >
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {sv.value}
                        </span>
                        {sv.unit && (
                          <span className="text-sm text-slate-500 dark:text-slate-400 ml-1">
                            {sv.unit}
                          </span>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="col-span-1 flex justify-center">
                        <ArrowRight
                          className={cn(
                            "w-4 h-4",
                            isUnsupported
                              ? "text-slate-300 dark:text-slate-600"
                              : "text-slate-400",
                          )}
                        />
                      </div>

                      {/* Base Value Dropdown */}
                      <div className="col-span-4">
                        {isUnsupported ? (
                          <span className="text-sm italic text-slate-400 dark:text-slate-500">
                            Marked as unsupported
                          </span>
                        ) : (
                          <div className="relative">
                            <select
                              data-testid={`mapping-select-${sv.id}`}
                              value={currentMapping || ""}
                              onChange={(e) =>
                                handleMappingChange(
                                  sv.id,
                                  e.target.value || null,
                                )
                              }
                              className={cn(
                                "w-full appearance-none px-3 py-2 pr-8 rounded-md text-sm",
                                "bg-white dark:bg-slate-800 border",
                                "text-slate-900 dark:text-slate-100",
                                "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                                hasError
                                  ? "border-amber-300 dark:border-amber-700"
                                  : currentMapping
                                    ? "border-emerald-300 dark:border-emerald-700"
                                    : "border-slate-200 dark:border-slate-600",
                              )}
                            >
                              <option value="">Select base value...</option>
                              {baseValues.map((bv) => (
                                <option key={bv.id} value={bv.value}>
                                  {bv.value} - {bv.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          </div>
                        )}
                      </div>

                      {/* AI Hint */}
                      <div className="col-span-2">
                        {sv.aiHint && !isUnsupported && (
                          <div className="flex items-center gap-1">
                            <Sparkles className="w-3 h-3 text-indigo-400" />
                            <span className="text-xs text-indigo-600 dark:text-indigo-400">
                              {sv.aiHint}
                            </span>
                          </div>
                        )}
                        {sv.confidence > 0 && !isUnsupported && (
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">
                            {Math.round(sv.confidence * 100)}% conf
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-1">
                        <button
                          data-testid={`unsupported-btn-${sv.id}`}
                          onClick={() => handleMarkUnsupported(sv.id)}
                          className={cn(
                            "p-1.5 rounded-md transition-colors",
                            isUnsupported
                              ? "bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400"
                              : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300",
                          )}
                          title={
                            isUnsupported
                              ? "Remove unsupported"
                              : "Mark as unsupported"
                          }
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`note-btn-${sv.id}`}
                          onClick={() =>
                            setShowNoteFor(showNoteFor === sv.id ? null : sv.id)
                          }
                          className={cn(
                            "p-1.5 rounded-md transition-colors",
                            notes[sv.id]
                              ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400"
                              : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300",
                          )}
                          title="Add note"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Note Input (Expandable) */}
                      {showNoteFor === sv.id && (
                        <div className="col-span-12 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <textarea
                            data-testid={`note-input-${sv.id}`}
                            value={notes[sv.id] || ""}
                            onChange={(e) =>
                              handleAddNote(sv.id, e.target.value)
                            }
                            placeholder="Add a note about this mapping..."
                            className={cn(
                              "w-full px-3 py-2 text-sm rounded-md",
                              "bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
                              "text-slate-900 dark:text-slate-100",
                              "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                              "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                              "resize-none",
                            )}
                            rows={2}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Validation Message */}
              {!isComplete && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {totalValues - resolvedCount} value
                    {totalValues - resolvedCount !== 1 ? "s" : ""} still need to
                    be mapped or marked as unsupported
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Base Values */}
        <div className="col-span-3">
          <Card className="sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Base Values
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                  ({context.baseMarketplace})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {baseValues.map((bv) => {
                  const mappedSources = mappingCounts[bv.value] || [];
                  const mappedCount = mappedSources.length;

                  return (
                    <div
                      key={bv.id}
                      data-testid={`base-value-${bv.id}`}
                      className={cn(
                        "p-3 rounded-lg border",
                        mappedCount > 0
                          ? "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700",
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                          {bv.value}
                        </span>
                        {mappedCount > 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
                            {mappedCount} mapped
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {bv.label}
                      </p>
                      {mappedCount > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {mappedSources.map((srcId) => {
                            const srcValue = sourceValues.find(
                              (s) => s.id === srcId,
                            );
                            return (
                              <span
                                key={srcId}
                                className="px-1.5 py-0.5 text-[10px] rounded bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
                              >
                                {srcValue?.value}
                                {srcValue?.unit ? ` ${srcValue.unit}` : ""}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Mapping Rules
                </p>
                <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    Many-to-one allowed
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="w-3 h-3 text-rose-500" />
                    One-to-many NOT allowed
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Footer */}
      <div
        data-testid="sticky-footer"
        className={cn(
          "fixed bottom-0 left-64 right-0 z-20",
          "bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700",
          "px-8 py-4 shadow-lg",
        )}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center",
                  isComplete
                    ? "bg-emerald-100 dark:bg-emerald-900"
                    : "bg-amber-100 dark:bg-amber-900",
                )}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    isComplete
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-amber-700 dark:text-amber-300",
                  )}
                >
                  {isComplete
                    ? "All values resolved"
                    : `${totalValues - resolvedCount} remaining`}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {mappedCount} mapped • {unsupportedCount} unsupported
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="cancel-btn"
              variant="outline"
              onClick={handleCancel}
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              data-testid="save-btn"
              onClick={handleSave}
              disabled={!isComplete || isSaved}
            >
              <Save className="w-4 h-4" />
              {isSaved ? "Saved" : "Save Value Mapping"}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
